import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  Divider,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { globalAdsSettingsAPI } from '../services/api';

const AdsSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [settings, setSettings] = useState({
    success: true,
    platform: 'android',
    timeout_ads: 100,
    show_start_ads: true,
    show_ads_first_open_channel: true,
    global_timeout_show_ads: 5,
    ads_instream_count: 1,
    ads_instream_tv_count: 1,
    timeout_ad_load: 10,
    ad_refresh_retry_timeout: 10800,
    skip_delay_instream: 5,
    skip_delay_instream_mytarget: 5,
    interstitial_native_tv_settings: {
      max_repeat_blocks: 1,
      skip_delay: 10,
      require_manual_close: true,
      refresh_interval_random_range_sec: [5, 15]
    }
  });

  useEffect(() => {
    loadGlobalSettings();
  }, []);

  const loadGlobalSettings = async () => {
    try {
      setLoading(true);
      const response = await globalAdsSettingsAPI.get();
      const loadedSettings = response.data.settings;
      
      // Мержим с дефолтными значениями чтобы не потерять поля
      setSettings(prev => ({
        ...prev,
        ...loadedSettings,
        interstitial_native_tv_settings: {
          max_repeat_blocks: 1,
          skip_delay: 10,
          require_manual_close: true,
          refresh_interval_random_range_sec: [5, 15],
          ...(loadedSettings.interstitial_native_tv_settings || {})
        }
      }));
    } catch (err) {
      setError('Ошибка загрузки глобальных настроек');
      console.error('Error loading global settings:', err);
    } finally {
      setLoading(false);
    }
  };



  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(false);
  };

  const handleNativeTVSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      interstitial_native_tv_settings: {
        ...(prev.interstitial_native_tv_settings || {}),
        [field]: value
      }
    }));
    setSuccess(false);
  };

  const handleRefreshIntervalChange = (index, value) => {
    setSettings(prev => {
      const currentSettings = prev.interstitial_native_tv_settings || {};
      const currentRange = currentSettings.refresh_interval_random_range_sec || [5, 15];
      const newRange = [...currentRange];
      // Разрешаем пустое значение для ввода, но сохраняем как число
      newRange[index] = value === '' ? '' : parseInt(value);
      return {
        ...prev,
        interstitial_native_tv_settings: {
          ...currentSettings,
          refresh_interval_random_range_sec: newRange
        }
      };
    });
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Подготавливаем настройки для сохранения
      const settingsToSave = {
        ...settings,
        interstitial_native_tv_settings: {
          ...settings.interstitial_native_tv_settings,
          // Конвертируем пустые строки в числа
          refresh_interval_random_range_sec: (settings.interstitial_native_tv_settings?.refresh_interval_random_range_sec || [5, 15]).map(
            v => typeof v === 'string' && v === '' ? 0 : (typeof v === 'string' ? parseInt(v) : v)
          )
        }
      };

      await globalAdsSettingsAPI.update({ settings: settingsToSave });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Ошибка сохранения настроек');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок с кнопкой назад */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/ads')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Глобальные рекламные настройки
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Настройки успешно сохранены!
        </Alert>
      )}



      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Глобальные рекламные настройки
        </Typography>

          <Grid container spacing={3}>
            {/* Основные настройки */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Основные настройки
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.success}
                    onChange={(e) => handleSettingChange('success', e.target.checked)}
                  />
                }
                label="Включить рекламу"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Платформа"
                value={settings.platform}
                onChange={(e) => handleSettingChange('platform', e.target.value)}
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="android">Android</option>
                <option value="ios">iOS</option>
                <option value="web">Web</option>
              </TextField>
            </Grid>

            {/* Таймауты */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Настройки таймаутов
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Таймаут рекламы (сек)"
                type="number"
                value={settings.timeout_ads}
                onChange={(e) => handleSettingChange('timeout_ads', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Глобальный таймаут показа рекламы (сек)"
                type="number"
                value={settings.global_timeout_show_ads}
                onChange={(e) => handleSettingChange('global_timeout_show_ads', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Таймаут загрузки рекламы (сек)"
                type="number"
                value={settings.timeout_ad_load}
                onChange={(e) => handleSettingChange('timeout_ad_load', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Таймаут повтора загрузки рекламы (сек)"
                type="number"
                value={settings.ad_refresh_retry_timeout}
                onChange={(e) => handleSettingChange('ad_refresh_retry_timeout', parseInt(e.target.value))}
              />
            </Grid>

            {/* Настройки показа */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Настройки показа рекламы
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.show_start_ads}
                    onChange={(e) => handleSettingChange('show_start_ads', e.target.checked)}
                  />
                }
                label="Показывать стартовую рекламу"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.show_ads_first_open_channel}
                    onChange={(e) => handleSettingChange('show_ads_first_open_channel', e.target.checked)}
                  />
                }
                label="Показывать рекламу при первом открытии канала"
              />
            </Grid>

            {/* Instream реклама */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Настройки Instream рекламы
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Количество Instream рекламы (мобильные)"
                type="number"
                value={settings.ads_instream_count}
                onChange={(e) => handleSettingChange('ads_instream_count', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Количество Instream рекламы (ТВ)"
                type="number"
                value={settings.ads_instream_tv_count}
                onChange={(e) => handleSettingChange('ads_instream_tv_count', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Задержка пропуска Instream (сек)"
                type="number"
                value={settings.skip_delay_instream}
                onChange={(e) => handleSettingChange('skip_delay_instream', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Задержка пропуска Instream MyTarget (сек)"
                type="number"
                value={settings.skip_delay_instream_mytarget}
                onChange={(e) => handleSettingChange('skip_delay_instream_mytarget', parseInt(e.target.value))}
              />
            </Grid>

            {/* Настройки нативной рекламы для Android TV */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Настройки нативной рекламы для Android TV
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Максимальное количество повторов блоков"
                type="number"
                value={settings.interstitial_native_tv_settings?.max_repeat_blocks || 1}
                onChange={(e) => handleNativeTVSettingChange('max_repeat_blocks', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Задержка пропуска (сек)"
                type="number"
                value={settings.interstitial_native_tv_settings?.skip_delay || 10}
                onChange={(e) => handleNativeTVSettingChange('skip_delay', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.interstitial_native_tv_settings?.require_manual_close || false}
                    onChange={(e) => handleNativeTVSettingChange('require_manual_close', e.target.checked)}
                  />
                }
                label="Требовать ручное закрытие"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Интервал обновления: мин (сек)"
                  type="number"
                  value={settings.interstitial_native_tv_settings?.refresh_interval_random_range_sec?.[0] || 5}
                  onChange={(e) => handleRefreshIntervalChange(0, e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Интервал обновления: макс (сек)"
                  type="number"
                  value={settings.interstitial_native_tv_settings?.refresh_interval_random_range_sec?.[1] || 15}
                  onChange={(e) => handleRefreshIntervalChange(1, e.target.value)}
                />
              </Box>
            </Grid>

            {/* Кнопка сохранения */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  color={success ? "success" : "primary"}
                >
                  {saving ? 'Сохранение...' : success ? '✓ Сохранено' : 'Сохранить настройки'}
                </Button>
              </Box>
            </Grid>
          </Grid>
      </Paper>
    </Box>
  );
};

export default AdsSettingsPage;
